// Detection Events
export enum DetectionType {
  WEBRTC = 'webrtc',
  ECDN11 = 'ecdn11',
  PLATFORM = 'platform', // Desktop version for teams.
  RUNTIME = 'runtime',
  REPORTER = 'reporter',
  NO_PLAYBACK = 'noPlaybackMethod'
}

export enum PrimingType {
  PREFETCH = 'prefetch',
  PUBLISH = 'publish',
  PUBLISH_CHECK = 'publishCheck',
  SESSION = 'session',
  RESTART = 'restart'
}

export enum ComponentCode {
  CONTENT = 2000,
  ELEVEN_AGENT = 2001,
  SDK = 2002,
  SIGNAL = 2003,
  STATS = 2004,
  TEN_AGENT = 2005,
  WEBRTC_STATS = 2006
}

export enum StatusCode {
  CONNECTION_ERROR = 3000,
  NO_PLAYBACK_FOUND = 3001
}

export enum ReportingState {
  CLEANED, // Reporter has cleaned up because of transition from page
  ENDED, // Received an ended event and sent a report (sends report immediately).
  PAUSED, // Received a pause event and sent a report.
  PAUSING, // Received a pause event but not yet sent a report.
  PLAY, // Received a play event from a paused state or unstarted state
  PLAYING, // Received a playing event and sending reports.
  RESTART, // Was in an ENDED state and received a seeking or play event.
  WAITING // We've just started out and have not received a play event.
}

export interface RuntimeDetectionEventData {
  componentCode: ComponentCode;
  error?: {
    statusCode: number;
  };
}

// See: common/Reporter/constants.ts: StdPlayerEvents and NonStdPlayerEvents
export type PlayerEvent =
  | { t?: number; ts: number; e: number } // No Metadata events
  | { t?: number; ts: number; e: number; b: number } // Bitrate Changed
  | { t?: number; ts: number; e: number; dur: number; synthetic?: boolean } // Duration
  | { t?: number; ts: number; e: number; pos: number } // Position
  | { t?: number; ts: number; e: number; rate: number }; // Playback Rate Change

export interface BasePlayerEvent {
  e: number;
  b?: number;
  dur?: number;
  synthetic?: number;
  pos?: number;
  rate?: number;
}

export enum PrimingError {
  DISABLED = 'disabled',
  MISSING_DATA = 'missingData',
  NO_PLAYBACK_METHOD = 'noValidPlaybackMethodFound',
  NOT_CAPABLE = 'notDeliveryCapable',
  UNKNOWN = 'unknown',
  UNSUPPORTED = 'unsupportedBrowser'
}

export enum DetectionReason {
  IMMEDIATE = 'sendImmediateReport',
  INTERVAL = 'intervalUpdate',
  NO_PLAY_EVENT = 'noPlayEvent',
  PAUSING = 'pausing',
  START = 'start',
  STOP = 'stop',
  TIMEOUT_SET = 'timeoutSet',
  TRIMMED = 'trimmed',
  UPDATE_AGENT = '11agentUpdate',
  VISIBILITY_CHANGE = 'visibilitychange'
}

export interface LastDetectionEvent {
  v?: string;
  date?: number;
  type?: DetectionType;
}

export interface RuntimeDetectionEvent {
  data?: RuntimeDetectionEventData;
  lastDetection?: LastDetectionEvent;
  method?: string;
  reason?: PrimingError | DetectionReason;
  status?: string | number;
  success: boolean;
  t?: number;
  type: DetectionType;
  v?: string;
}
export interface GeneralDetectionEvent {
  type: DetectionType;
  data?: Record<string, unknown>;
  t?: number;
}

export type DetectionEvent = GeneralDetectionEvent | RuntimeDetectionEvent;

export interface PrimingActionEvent {
  type: PrimingType;
  status?: string;
  t?: number;
}

export interface PrimingProgressEvent {
  progress: number;
  t?: number;
}

export interface PrimingCmdEvent {
  cmd: 'httpError' | 'handleError' | 'cancelStream' | 'switchToCDN';
  t?: number;
}

export type PrimingEvent =
  | PrimingActionEvent
  | PrimingProgressEvent
  | PrimingCmdEvent;
